/** AgentSkillsTabWidget — agent-scoped skill creation and version lifecycle. */

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Rocket, Save, Trash2 } from 'lucide-react';

import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { cn } from '@/shared/lib/cn';
import {
  useActivateAgentSkillVersion,
  useAttachAgentSkill,
  useAgentSkillsQuery,
  useAgentSkillVersionsQuery,
  useDeleteAgentSkillVersion,
  usePublishAgentSkillVersion,
  validateSkillFrontmatter,
  type AgentSkillCandidate,
  type SkillVersion,
} from '@/entities/skill';
import { CreateSkillDialog } from '@/features/skills/create-skill';
import TestChatPanelWidget from './TestChatPanelWidget';

function SkillStatusBadge({ skill }: { skill: AgentSkillCandidate }) {
  const status = skill.attachment?.status;
  return <Badge variant={status === 'approved' ? 'secondary' : 'outline'}>Attached</Badge>;
}

function versionLabel(version: SkillVersion): string {
  return `v${version.versionNumber}${version.isActive ? ' active' : ' draft'}`;
}

export default function AgentSkillsTabWidget() {
  const { agentId } = useParams<{ agentId: string }>();
  const { data, isLoading, isError, refetch } = useAgentSkillsQuery(agentId ?? null);
  const { attach, error } = useAttachAgentSkill(agentId ?? null);
  const skills = useMemo(() => data?.skills ?? [], [data?.skills]);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const selectedSkill = skills.find((skill) => skill.id === selectedSkillId) ?? skills[0] ?? null;
  const selectedId = selectedSkill?.id ?? null;
  const { data: versions = [], isLoading: versionsLoading } = useAgentSkillVersionsQuery(agentId ?? null, selectedId);
  const { publish, isPending: isSaving, error: saveError } = usePublishAgentSkillVersion(agentId ?? null, selectedId);
  const { activate, isPending: isActivating, error: activateError } = useActivateAgentSkillVersion(agentId ?? null, selectedId);
  const { deleteVersion, isPending: isDeleting, error: deleteError } = useDeleteAgentSkillVersion(agentId ?? null, selectedId);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [frontmatterError, setFrontmatterError] = useState<string | null>(null);

  const activeVersion = versions.find((version) => version.isActive) ?? null;
  const selectedVersion = versions.find((version) => version.id === selectedVersionId) ?? activeVersion ?? versions[0] ?? null;

  useEffect(() => {
    if (selectedSkillId === null && skills.length > 0) {
      setSelectedSkillId(skills[0].id);
    }
  }, [selectedSkillId, skills]);

  useEffect(() => {
    if (activeVersion) {
      setSelectedVersionId(activeVersion.id);
      setContent(activeVersion.skillMdContent);
    } else {
      setSelectedVersionId(null);
      setContent('');
    }
    setChangeSummary('');
    setFrontmatterError(null);
  }, [activeVersion]);

  const handleSelectVersion = (version: SkillVersion) => {
    setSelectedVersionId(version.id);
    setContent(version.skillMdContent);
    setChangeSummary('');
    setFrontmatterError(null);
  };

  const saveVersion = async (activateVersion: boolean) => {
    if (!selectedId || content.trim().length === 0) return;
    const validationError = validateSkillFrontmatter(content);
    if (validationError) {
      setFrontmatterError(validationError);
      return;
    }
    setFrontmatterError(null);
    const version = await publish({
      skillMdContent: content,
      changeSummary: changeSummary.trim() || undefined,
      activate: activateVersion,
    });
    setSelectedVersionId(version.id);
    setChangeSummary('');
  };

  const promoteSelected = async () => {
    if (!selectedVersion || selectedVersion.isActive) return;
    const version = await activate(selectedVersion.id);
    setSelectedVersionId(version.id);
  };

  const deleteSelected = async () => {
    if (!selectedVersion || selectedVersion.isActive) return;
    await deleteVersion(selectedVersion.id);
    setSelectedVersionId(activeVersion?.id ?? null);
  };

  const lifecycleError = saveError ?? activateError ?? deleteError;

  if (!agentId) {
    return (
      <p role="alert" className="pt-4 text-sm text-destructive">
        Agent id is missing from this settings route.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 pt-4 text-sm text-muted-foreground" role="status">
        <Loader2 data-icon="inline-start" className="animate-spin" />
        Loading agent skills...
      </div>
    );
  }

  if (isError) {
    return (
      <div role="alert" className="flex flex-col gap-2 pt-4 text-sm text-destructive">
        <p>Could not load skills for this agent.</p>
        <Button variant="outline" size="sm" className="self-start" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-4 pt-4" aria-labelledby="agent-skills-heading">
      <div className="flex flex-col gap-1">
        <h2 id="agent-skills-heading" className="text-lg font-semibold text-foreground">
          Agent skills
        </h2>
        <p className="text-sm text-muted-foreground">
          Create and test skills owned by this agent team. Marketplace discovery lives in the Skill Registry.
        </p>
      </div>
      <div>
        <Button onClick={() => setCreateOpen(true)}>New agent skill</Button>
      </div>

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error.message}
        </p>
      ) : null}

      {skills.length === 0 ? (
        <Card size="sm">
          <CardHeader>
            <CardTitle>No agent skills yet</CardTitle>
            <CardDescription>
              Create a skill here to attach it to this agent and start testing drafts.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(220px,320px)_minmax(0,1fr)]">
          <ul className="flex flex-col gap-2">
            {skills.map((skill) => {
              const selected = skill.id === selectedId;

              return (
                <li key={skill.id}>
                  <Card
                    size="sm"
                    className={cn(selected && 'border-primary bg-accent')}
                  >
                    <CardHeader>
                      <div className="min-w-0">
                        <CardTitle>{skill.name}</CardTitle>
                        <CardDescription>{skill.description || skill.slug}</CardDescription>
                      </div>
                      <CardAction>
                        <SkillStatusBadge skill={skill} />
                      </CardAction>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-2">
                      <Button
                        variant={selected ? 'secondary' : 'outline'}
                        onClick={() => setSelectedSkillId(skill.id)}
                        aria-label={`Edit lifecycle for ${skill.name}`}
                      >
                        Manage
                      </Button>
                    </CardContent>
                  </Card>
                </li>
              );
            })}
          </ul>

          {selectedSkill ? (
            <div className="flex min-w-0 flex-col gap-4">
              <Card size="sm">
                <CardHeader>
                  <div className="min-w-0">
                    <CardTitle>{selectedSkill.name}</CardTitle>
                    <CardDescription>{selectedSkill.description || selectedSkill.slug}</CardDescription>
                  </div>
                  <CardAction>
                    <SkillStatusBadge skill={selectedSkill} />
                  </CardAction>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                  {lifecycleError ? (
                    <p role="alert" className="text-sm text-destructive">
                      {lifecycleError instanceof Error ? lifecycleError.message : 'Skill update failed'}
                    </p>
                  ) : null}
                  {frontmatterError ? (
                    <p role="alert" className="text-sm text-destructive">
                      {frontmatterError}
                    </p>
                  ) : null}
                  <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_240px]">
                    <div className="flex min-w-0 flex-col gap-2">
                      <Label htmlFor={`skill-content-${selectedSkill.id}`}>SKILL.md</Label>
                      <Textarea
                        id={`skill-content-${selectedSkill.id}`}
                        value={content}
                        onChange={(event) => setContent(event.target.value)}
                        rows={16}
                        className="font-mono text-xs"
                      />
                      <Label htmlFor={`skill-summary-${selectedSkill.id}`}>Change summary</Label>
                      <Input
                        id={`skill-summary-${selectedSkill.id}`}
                        value={changeSummary}
                        onChange={(event) => setChangeSummary(event.target.value)}
                        placeholder="What changed?"
                      />
                    </div>

                    <div className="flex min-w-0 flex-col gap-2">
                      <p className="text-sm font-semibold">Versions</p>
                      {versionsLoading ? (
                        <p className="text-sm text-muted-foreground">Loading versions...</p>
                      ) : (
                        <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto">
                          {versions.map((version) => (
                            <li key={version.id}>
                              <Button
                                type="button"
                                variant={version.id === selectedVersion?.id ? 'secondary' : 'ghost'}
                                className="w-full justify-between"
                                onClick={() => handleSelectVersion(version)}
                              >
                                <span>{versionLabel(version)}</span>
                                <span className="text-xs text-muted-foreground">
                                  {version.changeSummary || ''}
                                </span>
                              </Button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      variant="outline"
                      disabled={isSaving || content.trim().length === 0}
                      onClick={() => void saveVersion(false)}
                    >
                      <Save />
                      Save draft
                    </Button>
                    <Button
                      disabled={isSaving || content.trim().length === 0}
                      onClick={() => void saveVersion(true)}
                    >
                      <Rocket />
                      Publish
                    </Button>
                    <Button
                      variant="outline"
                      disabled={isActivating || !selectedVersion || selectedVersion.isActive}
                      onClick={() => void promoteSelected()}
                    >
                      Promote selected
                    </Button>
                    <Button
                      variant="destructive"
                      disabled={isDeleting || !selectedVersion || selectedVersion.isActive}
                      onClick={() => void deleteSelected()}
                    >
                      <Trash2 />
                      Delete draft
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {selectedVersion ? (
                <TestChatPanelWidget
                  agentId={agentId}
                  promptVersionId={null}
                  draftSkillVersionIds={[selectedVersion.id]}
                  versionLabel={`${selectedSkill.name} ${versionLabel(selectedVersion)}`}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      <CreateSkillDialog
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(skill) => {
          void attach(skill.id).then(() => {
            setSelectedSkillId(skill.id);
          });
        }}
      />
    </section>
  );
}
